import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

// Shared direct Add-Progress confirmation modal. Reuses the existing
// POST /admin/members/{id}/progress endpoint (validation, operation_id idempotency,
// rank calculation and client email all live server-side — nothing duplicated here).
export function AddProgressModal({ member, onClose, onApplied }) {
  const [current, setCurrent] = useState(null);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [opId] = useState(() => crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`);

  useEffect(() => {
    let active = true;
    api
      .get(`/admin/members/${member.id}`)
      .then((res) => { if (active) setCurrent(res.data?.progression?.private?.progress ?? 0); })
      .catch(() => { if (active) setCurrent(0); });
    return () => { active = false; };
  }, [member.id]);

  const amt = Number(amount);
  const valid = amount !== "" && !Number.isNaN(amt) && amt > 0;
  const resulting = current != null && valid ? current + amt : null;

  const apply = async () => {
    if (!valid) { toast.error("Enter an amount greater than 0"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/members/${member.id}/progress`, {
        mode: "add",
        value: amt,
        operation_id: opId,
      });
      toast.success(
        data.duplicate_ignored
          ? "Duplicate ignored — already added"
          : data.promoted_to
            ? `Promoted to ${data.promoted_to}`
            : "Progress added",
      );
      onApplied?.(data);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4" data-testid="add-progress-modal">
      <div className="absolute inset-0 bg-black/70" onClick={() => !busy && onClose()} />
      <div className="card-lux relative w-full max-w-md p-6 sm:p-7">
        <p className="eyebrow-label mb-2">Add Progress</p>
        <h3 className="font-heading text-lg font-semibold text-white" data-testid="add-progress-name">{member.full_name}</h3>
        <p className="text-xs text-[#64748B] font-mono mb-5" data-testid="add-progress-identity">{member.uid || "—"} · {member.email || "—"}</p>
        <div className="grid grid-cols-3 gap-3 text-center mb-5">
          <div className="rounded-xl border border-[#222530] p-3">
            <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B] mb-1">Current</p>
            <p className="font-mono text-white text-sm" data-testid="add-progress-current">{current == null ? "…" : current}</p>
          </div>
          <div className="rounded-xl border border-[#222530] p-3">
            <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B] mb-1">Adding</p>
            <p className="font-mono text-[#D4AF37] text-sm" data-testid="add-progress-amount">{valid ? `+${amt}` : "—"}</p>
          </div>
          <div className="rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/[0.05] p-3">
            <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B] mb-1">Resulting</p>
            <p className="font-mono text-white text-sm" data-testid="add-progress-resulting">{resulting == null ? "—" : resulting}</p>
          </div>
        </div>
        <input
          type="number" step="any" min="0" autoFocus
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="Amount to add"
          className="input-lux w-full font-mono text-sm mb-2"
          data-testid="add-progress-input"
        />
        <p className="text-[10px] text-[#64748B] mb-5">Progress accumulates automatically; rank updates on its own. Members see percentage only.</p>
        <div className="flex gap-3">
          <button onClick={onClose} disabled={busy} className="btn-outline-gold flex-1 py-2.5 text-sm disabled:opacity-50" data-testid="add-progress-cancel">Cancel</button>
          <button onClick={apply} disabled={busy || !valid} className="btn-gold flex-1 py-2.5 text-sm disabled:opacity-50" data-testid="add-progress-confirm">
            {busy ? "Applying…" : "Confirm & Add"}
          </button>
        </div>
      </div>
    </div>
  );
}
